import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { approvalRequestsApi } from '@/api/services/approvalRequests';
import { ApprovalRequestType } from '@/api/types/approvalRequests';
import { usePendingRequestsHub } from './usePendingRequestsHub';

// Live pending-request counts for the sidebar, keyed by nav group key:
//   me-requests       = Match + Unmatch transaction requests (Matching Engine Requests)
//   providers-requests = provider-change requests (Providers Requests)
// Initial counts via GET pending-count per type; kept live by the pending-requests hub.
export function usePendingRequestsBadges(canMe: boolean, canProviders: boolean): Record<string, number | null> {
  const qc = useQueryClient();
  const [counts, setCounts] = useState<Record<number, number>>({});

  const refresh = useCallback(async () => {
    const types: ApprovalRequestType[] = [];
    if (canProviders) types.push(ApprovalRequestType.ProviderChange);
    if (canMe) types.push(ApprovalRequestType.MatchTransactions, ApprovalRequestType.UnmatchTransactions);
    const results = await Promise.allSettled(
      types.map((t) =>
        qc.fetchQuery({
          queryKey: ['approval-pending-count', t],
          queryFn: () => approvalRequestsApi.getPendingCount(t),
          staleTime: 0,
        }),
      ),
    );
    setCounts((prev) => {
      const next = { ...prev };
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') next[types[i]] = Number(r.value) || 0;
      });
      return next;
    });
  }, [canMe, canProviders, qc]);

  useEffect(() => {
    if (canMe || canProviders) void refresh();
  }, [canMe, canProviders, refresh]);

  usePendingRequestsHub({
    enabled: canMe || canProviders,
    onPendingCountChanged: (requestType, count) =>
      setCounts((prev) => ({ ...prev, [requestType]: Number(count) || 0 })),
  });

  const me = (counts[ApprovalRequestType.MatchTransactions] ?? 0) + (counts[ApprovalRequestType.UnmatchTransactions] ?? 0);
  const providers = counts[ApprovalRequestType.ProviderChange] ?? 0;
  return {
    'me-requests': canMe ? me : null,
    'providers-requests': canProviders ? providers : null,
  };
}
